<template>
  <div
    class="t-sidebar-menu-item"
    :class="{
      'is-active': menuItem.pathName === currentRouteName,
      'is-collapse': isCollapse,
      'is-disabled': menuItem?.isDisabled
    }"
  >
    <div
      v-if="menuItem.isActive"
      class="t-sidebar-menu-item-content"
      :class="{ 'is-open': menuItem.isOpen && menuItem.hasChild }"
      @click.stop="onClickMenuItem"
    >
      <span class="t-sidebar-menu-icon">
        <slot name="t-sidebar-menu-icon" />
      </span>
      <span
        class="t-sidebar-menu-title"
        :class="{
          'is-font-medium': menuItem.isFontMedium
        }"
      >
        <slot name="t-sidebar-menu-title">
          {{ t(menuItem.textKey) }}
        </slot>
      </span>
      <span v-if="menuItem.hasChild" class="ticon-arrow" :class="menuItem.isOpen ? 'up' : 'down'" />
    </div>

    <ul
      v-if="menuItem.hasChild && menuItem.isOpen"
      class="t-sidebar-child-menu"
      :class="{
        'is-open': menuItem.isOpen
      }"
    >
      <template v-for="(child, i) in menuItem.childrens">
        <li
          v-if="child.isActive"
          :key="i"
          class="t-sidebar-child-menu-item"
          :class="[
            currentRouteName == child.pathName ? 'is-active' : '', {
            'is-disabled': child?.isDisabled    
          }]"
          @click="
            onClickChildNavItem({
              childItem: child,
              childItemIndex: i,
              menuItem: menuItem
            })
          "
        >
          {{ t(child.textKey) }}
        </li>
      </template>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue"

import type { IntMenuItem, IntChildEmitPayload } from "./IntSidebarNav"

import { useI18n } from "vue-i18n"

const { t } = useI18n()

const emit = defineEmits<{
  (e: "onClickMenuItem", payload: IntMenuItem): void
  (e: "onClickChildNavItem", payload: IntChildEmitPayload): void
}>()

const props = defineProps({
  menuItem: {
    type: Object as PropType<IntMenuItem>,
    required: true
  },
  isActive: {
    type: Boolean,
    default: false
  },
  currentRouteName: {
    type: String,
    default: ""
  },
  isCollapse: {
    type: Boolean,
    default: false
  }
})

function onClickMenuItem(): void {
  if(!props.menuItem?.isDisabled) 
    emit("onClickMenuItem", props.menuItem)
}

function onClickChildNavItem(childMenuItem: IntChildEmitPayload): void {
  if (!childMenuItem.childItem.isDisabled) emit("onClickChildNavItem", childMenuItem)
}
</script>

<style lang="scss" scoped>
$menu-item-padding-left: calc(calc(var(--scl-spacing-16) * 3) + 12px); // 60px
$menu-item-content-min-height: calc(calc(var(--scl-spacing-16) * 3) + 8px); // 56px
.t-sidebar-menu {
  &-item {
    display: flex;
    flex-direction: column;
    transition: all 0.4s ease;
    position: relative;

    &.is-active,
    &:not(.is-disabled):hover {
      .t-sidebar-menu-item-content {
        color: var(--telekom-color-text-and-icon-primary-standard);
        background-color: var(--telekom-color-text-and-icon-inverted-standard);
      }
    }

    &:hover {
      cursor: pointer;
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }

  &-icon {
    width: 20px;
    height: 20px;
    position: absolute;
    left: var(--scl-spacing-24);
  }

  .ticon-arrow {
    position: absolute;
    right: 21px;
    border-color: var(--telekom-color-text-and-icon-standard);
  }

  &-item-content {
    padding: var(--scl-spacing-16) 0 var(--scl-spacing-16) $menu-item-padding-left;
    width: 100%;
    font-size: var(--scl-font-size-16);
    line-height: 100%;
    color: var(--telekom-color-text-and-icon-standard);
    font-weight: var(--scl-font-weight-bold);
    display: flex;
    align-items: center;
    position: relative;
    min-height: $menu-item-content-min-height;

    &.is-open {
      // border-bottom: 1px solid var(--telekom-color-ui-faint);
    }
  }

  .t-sidebar-child-menu {
    & li {
      padding-top: var(--scl-spacing-8);
      padding-bottom: var(--scl-spacing-8);
      padding-left: $menu-item-padding-left;
      font-size: var(--scl-font-size-14);
      line-height: 140%;
    }

    &-item {
      font-size: var(--scl-font-size-16);
      color: var(--telekom-color-text-and-icon-standard);
      font-weight: var(--scl-font-weight-medium);

      &:not(.is-disabled):hover,
      &.is-active {
        color: var(--telekom-color-text-and-icon-primary-standard);
        text-decoration: none;
      }

      &.is-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
  .t-sidebar-menu-item {
    .t-sidebar-menu-title,
    .ticon-arrow {
      animation: ShowElementWithOpacityAnimation 0.2s forwards;
      white-space: nowrap;

      &.is-font-medium {
        font-weight: var(--scl-font-weight-medium);
      }
    }

    &.is-collapse {
      .t-sidebar-menu-title,
      .ticon-arrow {
        display: none;
        animation: HideElementWithOpacityAnimation 0.1s forwards;
      }
    }
  }
}
</style>
